/**
 * @file CommoraAuthShell.tsx
 * @description Каркас страниц вне кабинета в теме Commora (phase19.29):
 * вход, восстановление пароля, выбор кабинета, «нет доступных кабинетов»,
 * «нет доступа». Макет — ui-references/admin/commora-console/login-mockup.png.
 *
 * - С 1024px (lg): две половины — слева панель бренда (`CommoraBrandPanel`,
 *   ~55 %), справа содержимое страницы (~45 %) по центру своей половины.
 * - До 1024px: только содержимое с логотипом сверху на мягком градиенте
 *   темы. Панель бренда до 1024 не рендерится вовсе (не прячется CSS) —
 *   лишнего не строим.
 *
 * Включает тему (`useCabinetTheme`) и заголовок вкладки «<Страница> —
 * Commora». Логика страниц не меняется — каркас только разметка.
 *
 * ```tsx
 * <CommoraAuthShell tabTitle="Вход">…форма…</CommoraAuthShell>
 * ```
 */

import type { ReactNode } from "react";

import { CommoraBrandPanel } from "./CommoraBrandPanel";
import { CommoraLogo } from "./CommoraLogo";
import { useCabinetTheme } from "../../hooks/useCabinetTheme";
import { BELOW_LG_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";
import { cn } from "@/lib/utils";

interface Props {
  /** Название страницы для вкладки: «Вход» → «Вход — Commora». */
  tabTitle: string;
  /** Широкая колонка — для списка (выбор кабинета). */
  wide?: boolean;
  children: ReactNode;
}

export function CommoraAuthShell({ tabTitle, wide = false, children }: Props) {
  useCabinetTheme("commora", `${tabTitle} — Commora`);
  const belowLg = useMediaQuery(BELOW_LG_QUERY);

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[55fr_45fr]">
      {belowLg ? null : <CommoraBrandPanel />}
      <main className="commora-auth-side flex min-h-screen flex-col items-center justify-center px-4 py-10 sm:px-6">
        <div className={cn("flex w-full flex-col items-center gap-6", wide ? "max-w-xl" : "max-w-[440px]")}>
          <CommoraLogo size="xl" />
          {children}
        </div>
      </main>
    </div>
  );
}
